/* eslint-disable react/require-default-props */
import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useMedia } from '../../../../hooks/useMedia';
import { media as mediaTheme } from '../../../../components/themes/theme';
import { ButtonArea, VideoArea, VideoPlayer, VideoWrapper, } from '../../../molecules/VideoPlayer';
import { useIsViewportMobile } from '../../../../hooks/useIsViewport';
import { Spacing } from '../../../../components/Spacing';
import { ImageV2 } from '../../../../components/ImageV2';
import { Grid, GridArea } from '../../../../components/Grid';
import { CloudinaryVideoPlayer, VideoArea as CloundinaryVideoArea, } from '../../../molecules/CloudinaryVideoPlayer';
import { StyledImage } from '../../../../components/ImageV2/styles';
import { ColorContextProvider } from '../../../providers/ColorContext';
const STAGE_CONTENT_MIN_HEIGHT = '32rem';
const STAGE_HEADER_PADDING = '90px';
const STAGE_INNER_PADDING = '5rem';
const StyledAutoplayVideo = styled.video ``;
const BelowHeader = styled.div `
  ${({ hasMarginTop, paddingTop }) => {
    const padding = hasMarginTop ? paddingTop || STAGE_HEADER_PADDING : '0rem';
    const height = `calc(${STAGE_CONTENT_MIN_HEIGHT} + ${padding})`;
    return css `
      position: relative;
      max-width: 200rem;
      margin: 0 auto;

      ${hasMarginTop &&
        css `
        margin-top: -${padding};
        padding-top: ${padding};
      `}

      ${mediaTheme.tabletL `
        min-height: ${height};
        display: grid;
      `}
    `;
}}
`;
const BackgroundWrapper = styled.div `
  ${({ backgroundColor = 'transparent' }) => {
    return css `
      background-color: ${backgroundColor};
    `;
}}
`;
const AbsoluteMediaWrapper = styled.div `
  ${({ layout = 'half-grid', media, direction }) => {
    return css `
      align-items: center;
      bottom: -2rem;
      display: flex;
      height: 100%;
      margin: 0 auto;
      max-height: 100%;
      max-width: 80rem;
      position: relative;
      overflow: hidden;
      justify-content: ${!(media === null || media === void 0 ? void 0 : media.positionX)
        ? layout === 'half-grid'
            ? direction === 'ltr'
                ? 'right'
                : 'left'
            : 'center'
        : media.positionX};

      ${StyledAutoplayVideo},
      ${StyledImage} {
        max-width: fit-content;
        max-height: 100%;
        height: auto;
        width: 100%;
        align-self: ${(media === null || media === void 0 ? void 0 : media.positionY) || 'center'};

        ${mediaTheme.tabletL `
          width: auto;

          ${layout === 'half-grid' &&
        css `
              max-width: ${(media === null || media === void 0 ? void 0 : media.scaling) || 100}%;
            `}
        `}
        ${layout === 'half-screen' &&
        css `
          height: 100%;
          max-width: none;

          ${direction === 'ltr'
            ? css `
                margin-left: auto;
                right: 0;
              `
            : css `
                margin-right: auto;
              `}
        `}
      }

      // VIDEO PLAYER SUPPORT OVERWRITES
      ${VideoWrapper} {
        margin-bottom: -1.5rem;
        width: 100%;
      }

      ${layout === 'half-screen' &&
        css `
        ${VideoArea}, ${VideoWrapper}, ${CloundinaryVideoArea} {
          position: static;
        }

        ${ButtonArea} {
          bottom: -2rem;
        }
      `}

      ${mediaTheme.tabletL `
        position: absolute;
        width: 50%;
        bottom:0;
        top: 0;

        ${direction === 'rtl'
        ? css `
                left: 0;
              `
        : css `
                right: 0;
              `}

        // IMAGEV2 SUPPORT OVERWRITE
        // @FIXME: workaround - need fix class for NextJS build
        .image-v2-container {
          height: 100%;
          display: flex;
          justify-content: ${!(media === null || media === void 0 ? void 0 : media.positionX)
        ? layout === 'half-grid'
            ? direction === 'ltr'
                ? 'right'
                : 'left'
            : 'center'
        : media.positionX};
        }
      `}
    `;
}}
`;
const Wrapper = styled.div `
  margin: 0 auto;
  width: 100%;
  padding: 2rem 0;
  display: grid;
  max-width: 75rem;

  ${mediaTheme.tabletL `
    min-height: calc(${STAGE_CONTENT_MIN_HEIGHT} - ${STAGE_INNER_PADDING});
  `}

  ${({ isFullWidth }) => !isFullWidth &&
    css `
      position: relative;
    `}

  ${({ isCentered }) => isCentered &&
    css `
      text-align: center;
    `};
`;
// choose Media
export const Media = ({ media }) => {
    const isMobile = useIsViewportMobile();
    if (!media)
        return null;
    const currentMedia = isMobile ? media.mobile : media.desktop;
    // SupportedMedia: add any favorite MediaComponent confguration here
    switch (currentMedia.variant) {
        case 'image':
            return (React.createElement(ImageV2, Object.assign({ dataTestId: "media--image", crop: currentMedia.crop || 'fill', responsive: [400, 600, 1000, 2000] }, currentMedia)));
        case 'video--autoplay':
            return (React.createElement(StyledAutoplayVideo, { "data-testid": "media--video--autoplay", autoPlay: true, loop: true, muted: true, playsInline: true, controls: false, src: currentMedia.src }));
        case 'videoPlayer':
            return (React.createElement(VideoPlayer, Object.assign({ "data-testid": "media--videoPlayer", srcDesktop: currentMedia.src, crop: currentMedia.crop }, currentMedia)));
        case 'videoPlayer--cloudinary':
            return (React.createElement(CloudinaryVideoPlayer, Object.assign({}, currentMedia, { videoAlt: currentMedia.alt, cloudinaryPublicId: currentMedia.src })));
        default:
            return null;
    }
};
export const MediaCopyView = ({ children, backgroundColor, media, layout = 'half-grid', hasMarginTop, 'data-testid': dataTestId = 'MediaCopy', direction = 'ltr', paddingTop, isContentCentered, }) => {
    const theme = useTheme();
    const isMobile = useMedia({ maxWidth: theme.screenTabletL });
    const isFullWidth = layout === 'half-screen';
    return (React.createElement(ColorContextProvider, { backgroundColor: backgroundColor },
        React.createElement(BackgroundWrapper, { "data-testid": dataTestId, backgroundColor: backgroundColor },
            React.createElement(BelowHeader, { hasMarginTop: hasMarginTop, paddingTop: paddingTop },
                React.createElement(Wrapper, { "data-testid": `${dataTestId}__wrapper--${layout}`, isFullWidth: isFullWidth, isCentered: isContentCentered },
                    React.createElement(Grid, { hasGapH: true, columns: isMobile ? 1 : 2 },
                        React.createElement(GridArea, { colStart: isMobile ? undefined : direction === 'ltr' ? 0 : 1, rowStart: !isMobile ? undefined : 0, contentAlignV: "center" },
                            React.createElement(Spacing, { mx: "spacing-s" }, children)),
                        React.createElement(GridArea, { colStart: isMobile ? undefined : direction === 'ltr' ? 1 : 0, rowStart: !isMobile ? undefined : 1 }, media && (React.createElement(AbsoluteMediaWrapper, { layout: layout, media: media, direction: direction },
                            React.createElement(Media, { media: media }))))))))));
};
